import { create } from 'zustand';
import type {
  FormBrandOverride,
  FormConsentMode,
  FormDeliveryMode,
  FormFieldDefinition,
  FormFieldType,
} from '@engage/types';

/**
 * Local builder state for FormBuilderPage. Mirrors the form being edited
 * — name, path type, fields, branding override, consent, gated content —
 * and exposes selectors plus action helpers for the right-pane settings.
 *
 * The store is intentionally form-scoped (reset on mount) rather than
 * module-singleton. Auto-save is driven from the page component using a
 * `dirty` flag here.
 */

export interface BuilderState {
  formId: string | null;
  name: string;
  pathType: 'CANDIDATE' | 'CONTACT';
  slug: string;
  fields: FormFieldDefinition[];
  brandOverride: FormBrandOverride | null;
  consentMode: FormConsentMode | null;
  consentText: string | null;
  gatedDocumentId: string | null;
  gatedDeliveryMode: FormDeliveryMode | null;
  gatedDocumentTtlSeconds: number | null;
  gatedEmailSubject: string | null;
  selectedFieldKey: string | null;
  dirty: boolean;

  initFrom: (form: {
    id: string;
    name: string;
    path_type: 'CANDIDATE' | 'CONTACT';
    slug: string;
    brand_override: FormBrandOverride | null;
    consent_mode: FormConsentMode | null;
    consent_text: string | null;
    gated_document_id: string | null;
    gated_delivery_mode: FormDeliveryMode | null;
    gated_document_ttl_seconds: number | null;
    gated_email_subject: string | null;
  }, fields: FormFieldDefinition[]) => void;

  setName: (name: string) => void;
  setSlug: (slug: string) => void;
  addField: (type: FormFieldType) => void;
  /** Adds a field with a fixed canonical key (e.g. `first_name`) + label, so
   *  the submission maps to the matching contact field. No-op-ish if a field
   *  with that key already exists — it just selects the existing one. */
  addPresetField: (preset: { type: FormFieldType; key: string; label: string }) => void;
  removeField: (key: string) => void;
  moveField: (key: string, direction: 'up' | 'down') => void;
  updateField: (key: string, patch: Partial<FormFieldDefinition>) => void;
  selectField: (key: string | null) => void;
  setBrandOverride: (next: FormBrandOverride | null) => void;
  setConsent: (mode: FormConsentMode | null, text: string | null) => void;
  setGated: (input: {
    document_id?: string | null;
    delivery_mode?: FormDeliveryMode | null;
    ttl_seconds?: number | null;
    email_subject?: string | null;
  }) => void;
  markSaved: () => void;
}

export const useBuilderStore = create<BuilderState>((set) => ({
  formId: null,
  name: '',
  pathType: 'CONTACT',
  slug: '',
  fields: [],
  brandOverride: null,
  consentMode: null,
  consentText: null,
  gatedDocumentId: null,
  gatedDeliveryMode: null,
  gatedDocumentTtlSeconds: null,
  gatedEmailSubject: null,
  selectedFieldKey: null,
  dirty: false,

  initFrom: (form, fields) =>
    set({
      formId: form.id,
      name: form.name,
      pathType: form.path_type,
      slug: form.slug,
      fields,
      brandOverride: form.brand_override,
      consentMode: form.consent_mode,
      consentText: form.consent_text,
      gatedDocumentId: form.gated_document_id,
      gatedDeliveryMode: form.gated_delivery_mode,
      gatedDocumentTtlSeconds: form.gated_document_ttl_seconds,
      gatedEmailSubject: form.gated_email_subject,
      selectedFieldKey: null,
      dirty: false,
    }),

  setName: (name) => set({ name, dirty: true }),
  setSlug: (slug) => set({ slug, dirty: true }),

  addField: (type) =>
    set((s) => {
      const key = nextKey(s.fields, slugForType(type));
      const next = newFieldOfType(type, key, s.pathType === 'CANDIDATE');
      return { fields: [...s.fields, next], selectedFieldKey: key, dirty: true };
    }),

  addPresetField: (preset) =>
    set((s) => {
      const existing = s.fields.find((f) => f.key === preset.key);
      // Canonical keys must stay exact (the submission mapping matches on the
      // literal key), so we never auto-suffix — re-adding just re-selects.
      if (existing) return { selectedFieldKey: existing.key };
      const base = newFieldOfType(preset.type, preset.key, s.pathType === 'CANDIDATE');
      const next = { ...base, label: preset.label } as FormFieldDefinition;
      return { fields: [...s.fields, next], selectedFieldKey: preset.key, dirty: true };
    }),

  removeField: (key) =>
    set((s) => ({
      fields: s.fields
        .filter((f) => f.key !== key)
        .map((f) =>
          f.conditional_logic?.field_key === key
            ? { ...f, conditional_logic: undefined }
            : f
        ),
      selectedFieldKey: s.selectedFieldKey === key ? null : s.selectedFieldKey,
      dirty: true,
    })),

  moveField: (key, direction) =>
    set((s) => {
      const idx = s.fields.findIndex((f) => f.key === key);
      if (idx === -1) return s;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= s.fields.length) return s;
      const next = [...s.fields];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return { fields: next, dirty: true };
    }),

  updateField: (key, patch) =>
    set((s) => ({
      fields: s.fields.map((f) => (f.key === key ? ({ ...f, ...patch } as FormFieldDefinition) : f)),
      dirty: true,
    })),

  selectField: (key) => set({ selectedFieldKey: key }),

  setBrandOverride: (brandOverride) => set({ brandOverride, dirty: true }),

  setConsent: (mode, text) => set({ consentMode: mode, consentText: text, dirty: true }),

  setGated: (input) =>
    set((s) => ({
      gatedDocumentId:
        input.document_id !== undefined ? input.document_id : s.gatedDocumentId,
      gatedDeliveryMode:
        input.delivery_mode !== undefined ? input.delivery_mode : s.gatedDeliveryMode,
      gatedDocumentTtlSeconds:
        input.ttl_seconds !== undefined ? input.ttl_seconds : s.gatedDocumentTtlSeconds,
      gatedEmailSubject:
        input.email_subject !== undefined ? input.email_subject : s.gatedEmailSubject,
      dirty: true,
    })),

  markSaved: () => set({ dirty: false }),
}));

// ─── helpers ──────────────────────────────────────────────────────────────────

function slugForType(type: FormFieldType): string {
  return type.toLowerCase().replace(/_/g, '-');
}

function nextKey(existing: FormFieldDefinition[], base: string): string {
  let candidate = base.replace(/-/g, '_');
  let suffix = 1;
  while (existing.some((f) => f.key === candidate)) {
    suffix += 1;
    candidate = `${base.replace(/-/g, '_')}_${suffix}`;
  }
  return candidate;
}

function newFieldOfType(
  type: FormFieldType,
  key: string,
  isCandidatePath: boolean
): FormFieldDefinition {
  const common = {
    key,
    label: defaultLabel(type),
    required: false,
    updateable_on_candidate_path: isCandidatePath ? true : undefined,
    validation_rules: isCandidatePath ? { blank_out_protected: true } : undefined,
  };
  switch (type) {
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
    case 'EMAIL':
    case 'PHONE':
    case 'DATE':
    case 'ADDRESS':
      return { type, ...common } as FormFieldDefinition;
    case 'FILE_UPLOAD':
      // A file goes to the person's Documents as a resume unless the builder picks another
      // type, and never updates a profile field (JT1-1923).
      return {
        type,
        ...common,
        updateable_on_candidate_path: isCandidatePath ? false : undefined,
        validation_rules: undefined,
        jt_document_type: 'resume',
      } as FormFieldDefinition;
    case 'NUMBER':
      return { type, ...common } as FormFieldDefinition;
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return { type, ...common, options: [] } as FormFieldDefinition;
    case 'CONSENT_CHECKBOX':
      return { type, key, label: 'I agree', required: true } as FormFieldDefinition;
  }
}

function defaultLabel(type: FormFieldType): string {
  const labels: Record<FormFieldType, string> = {
    SHORT_TEXT: 'Short text',
    LONG_TEXT: 'Long text',
    EMAIL: 'Email',
    PHONE: 'Phone',
    NUMBER: 'Number',
    SINGLE_SELECT: 'Choose one',
    MULTI_SELECT: 'Choose any',
    DATE: 'Date',
    FILE_UPLOAD: 'Upload file',
    ADDRESS: 'Address',
    CONSENT_CHECKBOX: 'I agree',
  };
  return labels[type];
}

// ─── publish-readiness checks (mirrors api side; UX-only — server is authoritative) ──

export interface ReadinessIssue {
  message: string;
  focus: 'fields' | 'gated' | 'metadata';
}

export function checkPublishReadiness(state: BuilderState): ReadinessIssue[] {
  const issues: ReadinessIssue[] = [];
  if (state.fields.length === 0) {
    issues.push({ message: 'Add at least one field', focus: 'fields' });
  }
  const keys = new Set<string>();
  for (const f of state.fields) {
    if (keys.has(f.key)) {
      issues.push({ message: `Duplicate field key: ${f.key}`, focus: 'fields' });
    }
    keys.add(f.key);
  }
  if (state.pathType === 'CONTACT' && !state.fields.some((f) => f.type === 'EMAIL')) {
    issues.push({
      message: 'Contact-path forms need an Email field (it’s the dedupe key)',
      focus: 'fields',
    });
  }
  if (
    state.pathType === 'CANDIDATE' &&
    !state.fields.some((f) => f.updateable_on_candidate_path !== false)
  ) {
    issues.push({
      message: 'Candidate-path forms need at least one updateable field',
      focus: 'fields',
    });
  }
  const seen = new Set<string>();
  for (const f of state.fields) {
    if (f.conditional_logic && !seen.has(f.conditional_logic.field_key)) {
      issues.push({
        message: `Conditional logic on "${f.key}" refers to a later or unknown field`,
        focus: 'fields',
      });
    }
    seen.add(f.key);
  }
  if (state.gatedDocumentId && !state.gatedDeliveryMode) {
    issues.push({ message: 'Gated content needs a delivery mode', focus: 'gated' });
  }
  return issues;
}
